import {
  Breadcrumbs,
  Button,
  dismissToast,
  Inline,
  List,
  Notification,
  notifyWarning,
  Skeleton,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import {
  EmployeeStepIds,
  useCompensationConfiguration,
  useCreateEmployeeContractMutation,
  useEmployee,
  useEmployeeOnboardingProgressMutation,
  useEmployments,
} from '@omnipresentgroup/domains';
import { EMPLOYEE_ID_PARAM } from '@omnipresentgroup/shared';
import {
  CompensationSource,
  EmploymentStatus,
  OnboardingProgressStepStatus,
} from '@omnipresentgroup/types';
import { getCountryCode } from 'countries-list';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { environment } from '../../../../environment';
import { Loading } from '../../../components';
import {
  ADD_ADDITIONAL_PAY_PATH,
  EMPLOYEE_CONTRACT_PATH,
  EMPLOYEE_DETAILS_PATH,
} from '../../../constants';
import {
  AddContractualVariantPaymentModal,
  CONTRACT_STATUS_WHITELIST,
  VariantPaymentsList,
} from '../../../features/variant-payments';
import { useManagerUser } from '../../../hooks';

const EDIT_NOTICE_TITLE =
  'Please review and amend the list of additional compensation items';
const EDIT_NOTICE_DESC =
  "Add all allowances and/or bonuses you'd like to grant the employee. Mandatory items are set by default, but amounts can be amended. Note, these will all have a default effective start date of the contractual employment state. All values need to be entered as gross amounts.";
const VIEW_NOTICE_TITLE = 'Adjusting items post-onboarding?';
const VIEW_NOTICE_DESC =
  'To adjust or grant additional compensation (e.g., allowances, bonuses, commissions), use the additional pay section of the platform. Recurring adjustments will be reviewed by our support team for compliance, and documentation will be provided to signatories.';
const VIEW_NOTICE_ACTION = 'Add additional pay';

enum Mode {
  VIEW = 'view',
  EDIT = 'edit',
}

const TOAST_ID = 'contractual-compensation-submit-notice';

export type PageParameters = {
  employeeId: string;
};

export const PAGE_TITLE = 'Contractual compensation';

export const ContractualCompensation = () => {
  const { employeeId } = useParams<PageParameters>();

  const [searchParameters, setSearchParameters] = useSearchParams();

  const navigate = useNavigate();

  const modeParameter = (searchParameters.get('mode') || Mode.VIEW) as Mode;

  const { companyId } = useManagerUser();

  // Get employee data
  const {
    data: employee,
    isLoading: isLoadingEmployee,
    isSuccess: isSuccessEmployee,
  } = useEmployee({
    id: employeeId!,
    enabled: !!employeeId,
  });

  const employeeCountryCode = useMemo(
    () =>
      ((employee?.country?.name && getCountryCode(employee?.country?.name)) ||
        '') as string,
    [employee?.country?.name],
  );

  const employeeCurrency = useMemo(
    () => employee?.employmentDetails?.annualBaseSalary?.currency || '',
    [employee?.employmentDetails?.annualBaseSalary?.currency],
  );

  const employeeName = useMemo(
    () =>
      employee
        ? `${employee?.personalDetails?.firstName} ${employee?.personalDetails?.lastName}`
        : '-',
    [employee],
  );

  const navigateToEmployeePage = useCallback(() => {
    window.location.href = `${
      environment.apps.OMNIPLATFORM
    }${EMPLOYEE_DETAILS_PATH(employeeId)}`;
  }, [employeeId]);

  const navigateToEmployeeContractPage = useCallback(
    (contractId: string) => {
      window.location.href = `${
        environment.apps.OMNIPLATFORM
      }${EMPLOYEE_CONTRACT_PATH(employeeId, contractId)}`;
    },
    [employeeId],
  );

  const { mutate: updateOnboardingProgress } =
    useEmployeeOnboardingProgressMutation({
      employeeId: employeeId!,
    });

  const {
    mutate: createEmployeeContract,
    isPending: isCreatingEmployeeContract,
    isSuccess: isSuccessCreateEmployeeContract,
    isError: isErrorCreateEmployeeContract,
    data: createEmployeeContractData,
  } = useCreateEmployeeContractMutation({
    employeeId: employeeId!,
  });

  const {
    data: compensationConfig,
    isLoading: isLoadingCompensationConfig,
    isSuccess: isSuccessCompensationConfig,
  } = useCompensationConfiguration({
    countryCode: employeeCountryCode,
    source: CompensationSource.CONTRACTUAL,
    enabled: !!employeeCountryCode,
  });

  const {
    data: employments,
    isLoading: isLoadingEmployments,
    isSuccess: isSuccessEmployments,
  } = useEmployments({
    userId: employee?.userId || '',
    enabled: !!employee?.userId,
  });

  const isEmployeeInactive = useMemo(
    () =>
      employments
        ?.filter((employment) => employment.companyId === companyId)
        ?.every(
          (employment) => employment.status === EmploymentStatus.INACTIVE,
        ),
    [companyId, employments],
  );

  const handleSubmit = useCallback(() => {
    notifyWarning(
      {
        title: 'Please wait while your submission is being processed.',
        description:
          'You will be redirected to the contract review page soon or notified when the contract is ready for review.',
      },
      {
        autoClose: false,
        toastId: TOAST_ID,
      },
    );
    createEmployeeContract();
  }, [createEmployeeContract]);

  // Handle submit success
  useEffect(() => {
    if (isSuccessCreateEmployeeContract) {
      const contractId = createEmployeeContractData?.contractId;
      contractId
        ? navigateToEmployeeContractPage(contractId)
        : navigateToEmployeePage();
    }
  }, [
    createEmployeeContractData?.contractId,
    isSuccessCreateEmployeeContract,
    navigateToEmployeeContractPage,
    navigateToEmployeePage,
  ]);

  // Hide the submit notice on error
  useEffect(() => {
    isErrorCreateEmployeeContract && dismissToast(TOAST_ID);
  }, [isErrorCreateEmployeeContract]);

  const handleEdit = useCallback(() => {
    updateOnboardingProgress({
      stepModifications: [
        {
          id: EmployeeStepIds.COMPENSATION_REVIEWED,
          status: OnboardingProgressStepStatus.UNCHECKED,
        },
      ],
    });
    setSearchParameters((parameters) => {
      parameters.set('mode', Mode.EDIT);
      return parameters;
    });
  }, [setSearchParameters, updateOnboardingProgress]);

  const canEdit = useMemo(
    () =>
      isEmployeeInactive === false &&
      (!employee?.contracts ||
        employee.contracts.every((contract) =>
          CONTRACT_STATUS_WHITELIST.has(contract.status),
        )),
    [employee?.contracts, isEmployeeInactive],
  );

  const editModeEnabled = useMemo(
    () => modeParameter === Mode.EDIT && canEdit,
    [canEdit, modeParameter],
  );

  const isLoading = useMemo(
    () =>
      isLoadingEmployee || isLoadingCompensationConfig || isLoadingEmployments,
    [isLoadingCompensationConfig, isLoadingEmployee, isLoadingEmployments],
  );

  const isSuccess = useMemo(
    () =>
      isSuccessEmployee && isSuccessCompensationConfig && isSuccessEmployments,
    [isSuccessCompensationConfig, isSuccessEmployee, isSuccessEmployments],
  );

  const getNotificationDescription = useCallback(() => {
    return canEdit ? (
      compensationConfig?.description ? (
        <List
          showDisc
          gap="0"
          items={[
            {
              id: '1',
              name: EDIT_NOTICE_DESC,
            },
            {
              id: '2',
              name: compensationConfig.description,
            },
          ]}
          keyExtractor="id"
          render={(item) => (
            <Typography as="span" size="14">
              {item.name}
            </Typography>
          )}
        />
      ) : (
        EDIT_NOTICE_DESC
      )
    ) : (
      VIEW_NOTICE_DESC
    );
  }, [canEdit, compensationConfig?.description]);

  const PageActions = () => {
    return editModeEnabled ? (
      <Inline>
        <AddContractualVariantPaymentModal
          trigger={
            <Button
              variant="secondary"
              disabled={isLoading || !isSuccess || isCreatingEmployeeContract}
            >
              Add
            </Button>
          }
          employeeData={{
            id: employeeId!,
            currency: employeeCurrency,
            countryCode: employeeCountryCode,
          }}
        />
        <Button
          onClick={handleSubmit}
          disabled={isLoading || !isSuccess}
          loading={isCreatingEmployeeContract}
        >
          Generate contract
        </Button>
      </Inline>
    ) : (
      <Button
        onClick={handleEdit}
        disabled={
          !canEdit || isLoading || !isSuccess || isCreatingEmployeeContract
        }
      >
        Edit
      </Button>
    );
  };

  const PageHeader = () => {
    return (
      <Stack gap="0">
        <Inline justify="space-between" minH={40} align="center">
          <Inline gap="8" align="center">
            <Typography as="h2" hideParagraphSpacing size="24" weight="medium">
              {PAGE_TITLE}
            </Typography>
          </Inline>
          <PageActions />
        </Inline>
        <Typography as="span" size="14" color="subtle">
          {isLoadingEmployee ? <Skeleton width={200} /> : employeeName}
        </Typography>
      </Stack>
    );
  };

  return (
    <>
      <Stack gap="16">
        <Breadcrumbs
          crumbs={[
            { id: 'home', title: 'Home', onClick: () => navigate('/') },
            {
              id: 'employee',
              title: employeeName,
              onClick: navigateToEmployeePage,
            },
            {
              id: 'add-compensation-details',
              title: PAGE_TITLE,
              active: true,
            },
          ]}
        />
        <PageHeader />
      </Stack>
      {isLoading ? (
        <Loading />
      ) : (
        <Stack gap="24">
          <Notification
            id="contractual-compensation-notice"
            title={canEdit ? EDIT_NOTICE_TITLE : VIEW_NOTICE_TITLE}
            description={getNotificationDescription()}
            link={
              canEdit
                ? undefined
                : {
                    to: `${ADD_ADDITIONAL_PAY_PATH}?${EMPLOYEE_ID_PARAM}=${employeeId}`,
                    text: VIEW_NOTICE_ACTION,
                  }
            }
          />
          {isSuccessEmployee && (
            <VariantPaymentsList
              employeeData={{
                id: employeeId!,
                currency: employeeCurrency,
                countryCode: employeeCountryCode,
              }}
              source={CompensationSource.CONTRACTUAL}
              columnVisibility={{
                type: true,
                reasonNotes: true,
                recurrence: true,
                amount: true,
                effectiveDate: true,
                endDate: true,
                isMandatory: true,
                actions: editModeEnabled && !isCreatingEmployeeContract,
              }}
            />
          )}
        </Stack>
      )}
    </>
  );
};
